import * as React from "react";
import reviewLayout from "./ArtifactReviewLayout.module.css";
import { FocusedHeaderActionsContext } from "./FocusedHeaderActions";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

import { SourceRailSurface } from "../SourceRailSurface";

/**
 * FocusedViewLayout — DRY chrome shared by every workspace hub when it
 * enters "inline-viewer mode" (``?focus=<id>&msg=<message_id>`` set on
 * the URL — see ``documents.tsx`` / ``conversations.tsx`` /
 * ``calendar.tsx``).
 *
 * Layout (top→bottom, same for all hubs):
 *
 *   1. **Rich header** — icon badge + eyebrow line + title + optional
 *      subtitle. Back-arrow is integrated into the same row (top-left,
 *      ghost button — no extra breadcrumb row). Matches the
 *      ``CalendarEventPane`` / ``InboxDetailPane`` PaneHeader aesthetic
 *      so the chrome IS the rich header — no doppio-titolo with the
 *      inner pane (the inner panes drop their own PaneHeader, May 23,
 *      2026 UX audit feedback).
 *   2. ``<SourceRailSurface>`` chip strip — 48px when 2+ sources, else 0
 *   3. ``children`` — the per-hub viewer (ArtifactPane / InboxDetailPane
 *      / CalendarEventPane)
 *
 * The viewer body is wrapped in ``<AnimatePresence mode="popLayout">`` +
 * a motion.div keyed by ``focusId`` so swapping chips produces a 180ms
 * opacity fade with a 4px micro-rise — SOTA agentic-UI feedback (the
 * spec from Linear / Notion AI / Vercel v0). Respects
 * ``prefers-reduced-motion``: no animation when the user opts out.
 *
 * Three states share the same chrome — extracted as static factories
 * (``loading`` / ``missing``) so call sites don't re-declare the same
 * empty/loading copy in every hub.
 */
export interface FocusedViewLayoutProps {
  /**
   * Eyebrow label above the title — the tab the user is currently
   * focused inside (Italian, e.g. "Documenti", "Conversazioni"). Mixed
   * with optional ``eyebrowSuffix`` to render rich context (e.g.
   * "Documenti · DOCX · 24 KB" or "Calendario · Tra 30 minuti").
   */
  readonly tabLabel: string;
  /**
   * Optional eyebrow suffix appended after the tab label with a
   * separator. Used to surface relative-time / mime-type / channel
   * context without adding another chrome row.
   */
  readonly eyebrowSuffix?: string;
  /** Title of the focused item (filename, subject, event title…). */
  readonly title: string;
  /**
   * Optional subtitle row (time range, sender, file path…). Rendered
   * one tier smaller, single-truncated.
   */
  readonly subtitle?: React.ReactNode;
  /**
   * Optional icon for the badge to the left of the title block. When
   * omitted the badge slot is hidden and the title takes the full
   * width.
   */
  readonly icon?: React.ReactNode;
  /** Aria-label for the back chevron — Italian, hub-specific. */
  readonly backAriaLabel: string;
  /** Back-chevron handler — typically navigates with ``search: {}``. */
  readonly onBack?: () => void;
  /** ``?msg=`` from the URL — drives the SourceRail. */
  readonly messageId: string | null;
  /** ``?focus=`` from the URL — marks the active chip in the SourceRail. */
  readonly focusId: string | null;
  /**
   * Optional right-aligned action slot inside the header — typically
   * a kebab menu or a primary CTA (e.g. "Apri originale").
   */
  readonly headerActions?: React.ReactNode;
  /**
   * Chrome mode:
   *   - ``"rich"`` (default) — full header with icon badge, eyebrow,
   *     big title, optional subtitle. Inner pane should NOT render its
   *     own PaneHeader.
   *   - ``"minimal"`` — slim 32px row with just back-arrow + a tiny
   *     breadcrumb eyebrow. The inner pane is responsible for its own
   *     PaneHeader (used by ``InboxDetailPane`` which surfaces
   *     thread-level metadata — message_count, unread_count — that
   *     would be awkward to pre-fetch one layer up).
   */
  readonly chromeMode?: "rich" | "minimal";
  /**
   * The viewer body to render. Keyed by ``focusId`` inside an
   * ``AnimatePresence`` so chip-driven swaps fade-in nicely.
   */
  readonly children: React.ReactNode;
}

export const FocusedViewLayout: React.FC<FocusedViewLayoutProps> = ({
  tabLabel,
  eyebrowSuffix,
  title,
  subtitle,
  icon,
  backAriaLabel,
  onBack,
  messageId,
  focusId,
  headerActions,
  chromeMode = "rich",
  children,
}) => {
  const reducedMotion = useReducedMotion();
  const [headerHost, setHeaderHost] = React.useState<HTMLDivElement | null>(null);
  return (
    <FocusedHeaderActionsContext.Provider value={headerHost}>
    <div className={`flex h-full min-h-0 flex-col ${reviewLayout.focusedShell}`}>
      {chromeMode === "minimal" ? (
        /* Minimal chrome — slim 32px row with just back-arrow + a
           breadcrumb eyebrow. Used when the inner pane renders its
           own rich PaneHeader (e.g. ``InboxDetailPane`` which surfaces
           thread-level metadata that requires data we don't have one
           layer up). */
        <div className="flex h-8 shrink-0 items-center gap-2 border-hairline border-b px-4">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label={backAriaLabel}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-white/55 hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
            </button>
          ) : null}
          <span className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
            {tabLabel}
            {eyebrowSuffix ? ` · ${eyebrowSuffix}` : ""}
          </span>
          {headerActions ? (
            <div className="ml-auto flex shrink-0 items-center gap-1">{headerActions}</div>
          ) : null}
        </div>
      ) : (
        /* Rich single-row header — back-arrow integrated at the
           left, icon badge, eyebrow + title + optional subtitle.
           Compatto (~60px) ma con la stessa identità visiva del
           CalendarEventPane: badge cromato + eyebrow ando-orange
           uppercase + titolo grande. May 23, 2026 audit: il vecchio
           breadcrumb single-row era "troppo piatto" rispetto al pane
           dell'evento. */
        <div className="flex shrink-0 items-start gap-2.5 border-hairline border-b px-4 py-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label={backAriaLabel}
              className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-white/55 hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
            </button>
          ) : null}
          {icon ? (
            <span
              aria-hidden
              className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md bg-ando-orange-soft text-ando-orange"
            >
              {icon}
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            {/* Eyebrow — May 23 polish: dropped the decorative orange dot
                (it competed visually with the orange icon badge to the
                left, creating "two orange anchors"). Pure typography
                eyebrow matches Notion's doc-header pattern. */}
            <div className="truncate font-medium text-[10px] text-ando-orange uppercase tracking-[0.06em]">
              {tabLabel}
              {eyebrowSuffix ? ` · ${eyebrowSuffix}` : ""}
            </div>
            <h1 className="mt-0.5 truncate font-display font-semibold text-[17px] text-ink-1 leading-[1.25] tracking-[-0.01em]">
              {title}
            </h1>
            {subtitle ? (
              <div className="mt-1 truncate text-[12px] text-ink-3">{subtitle}</div>
            ) : null}
          </div>
          <div ref={setHeaderHost} className="ml-2 flex min-w-0 shrink-0 items-center gap-1" data-focused-header-actions>
            {headerActions}
          </div>
        </div>
      )}
      <SourceRailSurface messageId={messageId} activeFocusId={focusId} />
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={focusId ?? "empty"}
            className="absolute inset-0 overflow-y-auto"
            // May 23 polish — bumped y-translation from 4px → 8px and
            // duration 180 → 220ms with out-expo easing. The previous
            // micro-animation was below the JND on a 600-800px viewer
            // (UX audit P1 #10). Matches Linear page-swap cadence.
            initial={reducedMotion ? false : { opacity: 0, y: 8 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={{ duration: reducedMotion ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
    </FocusedHeaderActionsContext.Provider>
  );
};
FocusedViewLayout.displayName = "FocusedViewLayout";
